import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"; // Updated icon
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined"; // Icon for date picker
import { useSnackbar } from 'notistack';
import { useForm, Controller } from "react-hook-form";
import { DesktopDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FormButton } from "../../Button";
import { getReq, postReq } from "../../../apis/api";
import {
  DescriptionOutlined,
  PeopleOutline,
  PersonPinCircleOutlined,
  TitleOutlined,
} from "@mui/icons-material";

// P060: a template targets one or more user types (role ids) and, optionally, chosen people among them.
// SGEW 19 Oct (owner, question 24): the staff roles too (Notifier::TEMPLATE_TYPES; Admins get the system alerts)
const USER_TYPES = [
  [8, "Dealer"],
  [10, "Employees"],
  [9, "Customers"],
  [7, "Suppliers"],
  [2, "Sub Admin"],
  [3, "Accounts"],
  [4, "Purchase/Item Stock Mgr"],
  [5, "Supervisor"],
  [6, "Sales Manager"],
];
// P059: the tags the server fills for each recipient (Notifier::TAGS); a Sub Admin or Sales Manager has no ledger, so
// their {balance} reads "-"
const DYNAMIC_TAGS = "{name} {user_id} {balance} {due_date} {company}";
const TAGS_NOTE = "{balance} is \"-\" for Sub Admin and Sales Manager (no ledger)";

const typeIds = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((x) => Number(x.trim()))
    .filter((x) => x > 0);

const Add = ({ open, handleClose, selectedItemId, onItemAdded }) => {
  const [users, setUsers] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      user_type: [],
      schedule: new Date(),
      user_id: [],
    },
  });

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getReq(
          `fetchSingleNotification/${selectedItemId}`
        );

        if (response.status === 200) {
          const data = response.data[0];
          if (data) {
            reset({
              title: data.title,
              description: data.description,
              schedule: data.schedule ? new Date(data.schedule) : new Date(),
              user_name: data.user_name,
              user_id: [],
              user_type: typeIds(data.user_type),
            });
          }
        }
      } catch (err) {
        console.error("error", err);
      }
    };
    if (selectedItemId) {
      fetchData();
    }
  }, [selectedItemId, reset]);

  // Watch the schedule field
  const currentSchedule = watch("schedule");

  // Add this useEffect to update scheduleDate when form changes
  useEffect(() => {
    if (currentSchedule) {
      setScheduleDate(new Date(currentSchedule));
    }
  }, [currentSchedule]);

  // Function to check if schedule is in the future
  const isScheduleInFuture = () => {
    const now = new Date();
    return scheduleDate > now;
  };

  // P060: the active accounts of every chosen user type (the same people the template reaches)
  const selectedTypes = watch("user_type");
  const typesKey = (selectedTypes || []).join(",");
  useEffect(() => {
    if (selectedItemId) return;
    if (!typesKey) {
      setUsers([]);
      setValue("user_id", []);
      return;
    }
    let active = true;
    (async () => {
      try {
        const response = await getReq(`notifications/recipients?user_type=${typesKey}`);
        if (!active) return;
        const list = response?.data || [];
        setUsers(list);
        // people of a type that was taken off are no longer chosen
        const ids = new Set(list.map((u) => String(u.id)));
        setValue("user_id", (getValues("user_id") || []).filter((id) => id === "all" || ids.has(String(id))));
      } catch (err) {
        if (active) {
          setUsers([]);
          enqueueSnackbar(err?.response?.data?.message || "Could not load the users of these types", { variant: "error" });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [typesKey, selectedItemId, setValue, getValues, enqueueSnackbar]);

  const handleCancel = async () => {
    try {
      const response = await postReq(
        {
          id: selectedItemId,
        },
        "/cancelNotification"
      );
      if (response.status === 200) {
        enqueueSnackbar(
          response?.data?.message || "Notification cancelled successfully",
          { variant: "success" }
        );
        handleClose();
        setTimeout(() => {
          onItemAdded?.();
        }, 2000);
      } else {
        enqueueSnackbar(
          response?.data?.message || "Failed to cancel the notification",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("error", err);
      enqueueSnackbar(err?.response?.data?.message || "Failed to cancel the notification", { variant: "error" });
    }
  };

  const onSubmit = async (data) => {
    // "All users" or nobody picked: every active account of the chosen types (the server works out who)
    const picked = (data.user_id || []).filter((id) => id !== "all");
    const payload = {
      title: data.title,
      description: data.description,
      schedule: data.schedule,
      user_type: (data.user_type || []).join(","),
      ...((data.user_id || []).includes("all") || picked.length === 0 ? {} : { user_id: picked.join(",") }),
    };
    try {
      const response = await postReq(payload, "/createNotification");
      if (response.status === 200) {
        enqueueSnackbar(response?.data?.message || "Notification saved", { variant: "success" });

        setTimeout(() => {
          handleClose();
          onItemAdded?.();
        }, 2000);
      } else {
        enqueueSnackbar(response?.data?.message || "Error creating notification. Please try again.", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Error creating notification. Please try again.", { variant: "error" });
      console.error("API error:", error.response?.data || error.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "20px",
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          paddingBottom: "16px",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <NotificationsActiveIcon color="primary" /> {/* Updated icon */}
          {selectedItemId ? "Update Notification" : "Create Notification"}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: "#6b7280" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 4, py: 2 }}>
          <Controller
            name="title"
            control={control}
            rules={{ validate: (v) => String(v || "").trim() !== "" || "Title is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                placeholder="Enter Title"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TitleOutlined />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                error={!!errors.title}
                helperText={errors.title?.message}
                sx={{ mb: 3 }}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            rules={{ validate: (v) => String(v || "").trim() !== "" || "Description is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionOutlined />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                multiline
                placeholder="Enter Description"
                minRows={1}
                maxRows={4}
                error={!!errors.description}
                helperText={errors.description?.message || `Dynamic tags: ${DYNAMIC_TAGS} - ${TAGS_NOTE}`}
                sx={{ mb: 3 }}
              />
            )}
          />

          <Controller
            name="user_type"
            control={control}
            rules={{ validate: (v) => (v || []).length > 0 || "Choose at least one user type" }}
            render={({ field: { onChange, value } }) => (
              <TextField
                select
                label="User Type"
                value={value || []}
                onChange={(e) => onChange(e.target.value)}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    USER_TYPES.filter(([id]) => selected.includes(id)).map(([, name]) => name).join(", "),
                }}
                fullWidth
                disabled={Boolean(selectedItemId)}
                error={!!errors.user_type}
                helperText={errors.user_type?.message || "One or more types"}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonPinCircleOutlined />
                    </InputAdornment>
                  ),
                }}
              >
                {USER_TYPES.map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {selectedItemId && (
            <Controller
              name="user_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Recipients"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleOutline />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  multiline
                  placeholder="Enter Username"
                  minRows={1}
                  maxRows={4}
                  error={!!errors.user_name}
                  helperText={errors.user_name?.message}
                  sx={{ mb: 3 }}
                />
              )}
            />
          )}

          {!selectedItemId && (
            <Controller
              name="user_id"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  label="Select Recipients"
                  value={value || []}
                  onChange={(event) => {
                    const selected = event.target.value;
                    // "All Users" stands for every active account of the chosen types
                    onChange(selected.includes("all") && !(value || []).includes("all") ? ["all"] : selected.filter((id) => id !== "all"));
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      if (selected.includes("all")) {
                        return "All Users";
                      }
                      return users
                        .filter((user) => selected.includes(String(user.id)))
                        .map((user) => user.user_name)
                        .join(", ");
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                        },
                      },
                    },
                  }}
                  fullWidth
                  error={!!errors.user_id}
                  helperText={errors.user_id?.message || "Leave empty (or All Users) for everyone of the chosen types"}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleOutline />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem key="all" value="all">
                    <em>Select All Users</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={String(user.id)}>
                      {user.user_name}
                      {user.role_name ? ` (${user.role_name})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}

          {/* past days are greyed out; a time already gone today only means "at the next run" -
              disablePast flagged the dialog's own "now" red as soon as it opened */}
          <Controller
            name="schedule"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDateTimePicker
                  {...field}
                  label="Schedule"
                  format="dd-MM-yyyy hh:mm a" // the app's day-month-year order (it showed 10/17/2026)
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  slotProps={{ textField: { fullWidth: true } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Select Date & Time"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayOutlined />
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.schedule}
                      helperText={errors.schedule?.message}
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </DialogContent>

        {/* a notification already sent has nothing left to do - no empty footer bar */}
        {(!selectedItemId || isScheduleInFuture()) && (
          <DialogActions sx={{ px: 4, py: 2, borderTop: "1px solid #e0e0e0" }}>
            {!selectedItemId && (
              <FormButton
                func={handleSubmit(onSubmit)}
                title="Save Notification"
              />
            )}
            {selectedItemId && isScheduleInFuture() && (
              <FormButton
                type="button"
                func={handleCancel}
                title="Cancel Notification"
                customcls="sgew-btn-danger"
              />
            )}
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default Add;
